import { clx } from "@medusajs/ui"
import React from "react"

// Mediahawk dynamic-number placeholder. The `mediahawkNumber9471` class is
// the campaign-specific number ID — Mediahawk's `mhct.min.js` (loaded once in
// the root layout) scans the page on load and replaces the inner content of
// every matching span with a tracking-instrumented `tel:` anchor.
//
// The static `01202 725 677` link inside is the pre-hydration / no-JS / SEO
// fallback. After Mediahawk runs it is overwritten with the dynamic number.
export const MEDIAHAWK_NUMBER_CLASS = "mediahawkNumber9471 mhMobile"
// Display-only variant: Mediahawk's `Z()` only injects a clickable inner <a>
// when the span has the `mhMobile` class. Without it the dynamic number is
// inserted as a plain text node — exactly what we want when the surrounding
// element is already a tel: anchor (or a button) and click-to-call is
// provided by the ancestor.
export const MEDIAHAWK_NUMBER_DISPLAY_CLASS = "mediahawkNumber9471"
export const FALLBACK_TEL_DIGITS = "01202725677"
export const FALLBACK_TEL_DISPLAY = "01202 725 677"

type TelNumProps = {
  // Classes applied to the outer Mediahawk span (carry the original anchor's styling here)
  className?: string
  // Classes applied to the inner pre-hydration <a> fallback
  anchorClassName?: string
  // Optional override for the displayed fallback number text
  displayNumber?: string
  // Forwarded data-testid
  testId?: string
}

export default function TelNum({
  className,
  anchorClassName,
  displayNumber = FALLBACK_TEL_DISPLAY,
  testId,
}: TelNumProps) {
  return (
    <span
      className={clx(MEDIAHAWK_NUMBER_CLASS, className)}
      data-testid={testId}
    >
      <a href={`tel:${FALLBACK_TEL_DIGITS}`} className={anchorClassName}>
        {displayNumber}
      </a>
    </span>
  )
}

// Display-only React variant — for use inside an ancestor that is already
// a tel: anchor or styled button. Renders the number as text only; Mediahawk
// will replace the inner text with the dynamic number.
type TelNumTextProps = {
  className?: string
  displayNumber?: string
  testId?: string
}

export function TelNumText({
  className,
  displayNumber = FALLBACK_TEL_DISPLAY,
  testId,
}: TelNumTextProps) {
  return (
    <span
      className={[MEDIAHAWK_NUMBER_DISPLAY_CLASS, className]
        .filter(Boolean)
        .join(" ")}
      data-testid={testId}
    >
      {displayNumber}
    </span>
  )
}

// Equivalent HTML strings for inlining before `dangerouslySetInnerHTML`.
// Kept in sync with the component output above.
export const TELNUM_SPAN_HTML =
  `<span class="${MEDIAHAWK_NUMBER_CLASS}">` +
  `<a href="tel:${FALLBACK_TEL_DIGITS}">${FALLBACK_TEL_DISPLAY}</a>` +
  `</span>`
export const TELNUM_TEXT_SPAN_HTML =
  `<span class="${MEDIAHAWK_NUMBER_DISPLAY_CLASS}">${FALLBACK_TEL_DISPLAY}</span>`

const CLICKABLE_TOKEN_REGEX =
  /<telnum\s*\/?>|<telnum\s*>\s*<\/telnum\s*>/gi
const DISPLAY_TOKEN_REGEX =
  /<telnum-text\s*\/?>|<telnum-text\s*>\s*<\/telnum-text\s*>/gi
// Combined splitter that preserves which token matched: captures group 1 = clickable, group 2 = display
const ANY_TOKEN_REGEX =
  /(<telnum\s*\/?>|<telnum\s*>\s*<\/telnum\s*>)|(<telnum-text\s*\/?>|<telnum-text\s*>\s*<\/telnum-text\s*>)/gi

// Replace placeholder tokens in a raw HTML string:
//   <telnum />       → clickable mhMobile span (own tel: link)
//   <telnum-text />  → display-only span (no inner anchor; surrounding
//                      element is expected to be the tel: link)
export function hydrateTelnumHtml(html: string): string {
  return html
    .replace(CLICKABLE_TOKEN_REGEX, TELNUM_SPAN_HTML)
    .replace(DISPLAY_TOKEN_REGEX, TELNUM_TEXT_SPAN_HTML)
}

// Plain-text-field renderer: splits a string on either placeholder token
// and emits the matching React component between segments.
export function renderTelnumText(
  text: string | undefined | null
): React.ReactNode {
  if (text == null || text === "") return text
  if (
    !CLICKABLE_TOKEN_REGEX.test(text) &&
    !DISPLAY_TOKEN_REGEX.test(text)
  ) {
    // Reset lastIndex on the global regexes we just `test`ed
    CLICKABLE_TOKEN_REGEX.lastIndex = 0
    DISPLAY_TOKEN_REGEX.lastIndex = 0
    return text
  }
  CLICKABLE_TOKEN_REGEX.lastIndex = 0
  DISPLAY_TOKEN_REGEX.lastIndex = 0

  const nodes: React.ReactNode[] = []
  let lastIndex = 0
  let key = 0
  let match: RegExpExecArray | null
  const splitter = new RegExp(ANY_TOKEN_REGEX.source, "gi")
  while ((match = splitter.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index))
    }
    if (match[1]) {
      nodes.push(<TelNum key={`tn-${key++}`} />)
    } else if (match[2]) {
      nodes.push(<TelNumText key={`tn-${key++}`} />)
    }
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex))
  return nodes
}
